<template>
  <el-dialog
    :visible.sync="visible"
    title=""
    width="100%"
    fullscreen
    :close-on-click-modal="false"
    :show-close="false"
    custom-class="send-review-dialog"
    @close="handleClose"
  >
    <!-- Header -->
    <div class="send-review__header">
      <p class="send-review__title">Revisar antes de enviar</p>
      <div class="d-flex align-items-center" style="gap: 10px">
        <button
          class="btn md__btn-primary"
          :disabled="!canSend || sending"
          @click="confirmSend"
        >
          <i class="el-icon-loading" v-if="sending" />
          <i class="el-icon-s-promotion" v-else />
          Enviar a firmar
        </button>
        <button class="send-review__close" @click="handleClose">
          <i class="el-icon-close" />
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="send-review__body">
      <div class="send-review__grid">
        <!-- Left: Document summary -->
        <div class="send-review__section">
          <div class="send-review__card">
            <p class="send-review__card-title">Documento</p>
            <div class="send-review__row">
              <span class="send-review__label">Nombre</span>
              <span class="send-review__value">{{ document.name }}</span>
            </div>
            <div class="send-review__row">
              <span class="send-review__label">Tipo</span>
              <span class="send-review__value">{{
                readType(document.type)
              }}</span>
            </div>
            <div class="send-review__row" v-if="document.expires_at">
              <span class="send-review__label">Expira</span>
              <span class="send-review__value">{{ document.expires_at }}</span>
            </div>
            <div class="send-review__row" v-if="document.source">
              <span class="send-review__label">Origen</span>
              <span class="send-review__value">{{
                readSource(document.source)
              }}</span>
            </div>
          </div>

          <!-- Email message (optional) -->
          <div class="send-review__card mt-3">
            <p class="send-review__card-title">Mensaje del email (opcional)</p>
            <el-input
              type="textarea"
              v-model="emailMessage"
              :rows="4"
              placeholder="Agrega un mensaje personalizado que se incluirá en el email de notificación a los firmantes..."
            />
          </div>

          <!-- Reminder -->
          <div class="send-review__card mt-3">
            <p class="send-review__card-title">Recordatorio automático</p>
            <el-select
              v-model="reminderDays"
              style="width: 100%"
              placeholder="Sin recordatorio"
            >
              <el-option :value="0" label="Sin recordatorio" />
              <el-option :value="1" label="Cada día" />
              <el-option :value="3" label="Cada 3 días" />
              <el-option :value="7" label="Cada 7 días" />
            </el-select>
          </div>
        </div>

        <!-- Right: Signers -->
        <div class="send-review__section">
          <div class="send-review__card">
            <p class="send-review__card-title">
              Firmantes ({{ signers.length }})
            </p>

            <div v-if="signers.length">
              <div
                class="send-review__signer"
                v-for="(signer, idx) in signers"
                :key="signer.id"
              >
                <div class="send-review__signer-order">{{ idx + 1 }}</div>
                <div class="send-review__signer-info">
                  <p class="send-review__signer-name">{{ signer.name }}</p>
                  <p class="send-review__signer-email">{{ signer.email }}</p>
                </div>
                <span class="send-review__signer-method">
                  {{ signer.sign_method === "certificate" ? "Cert." : "Firma" }}
                </span>
              </div>
            </div>

            <div v-else class="send-review__empty">
              <i class="el-icon-warning-outline" />
              <p>No se han agregado firmantes.</p>
              <p>Vuelve al detalle del documento para agregar firmantes.</p>
            </div>
          </div>

          <!-- Warnings -->
          <div
            class="send-review__card send-review__card--warning mt-3"
            v-if="warnings.length"
          >
            <p class="send-review__card-title">
              <i class="el-icon-warning" /> Advertencias
            </p>
            <ul class="send-review__warnings">
              <li v-for="(w, i) in warnings" :key="i">{{ w }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "DocumentSendReview",
  props: {
    show: { type: Boolean, default: false },
    document: { type: Object, default: () => ({}) },
    signers: { type: Array, default: () => [] },
    sending: { type: Boolean, default: false }
  },
  data() {
    return {
      emailMessage: "",
      reminderDays: 3
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit("update:show", val);
      }
    },
    canSend() {
      return this.signers.length > 0;
    },
    warnings() {
      const list = [];
      if (!this.signers.length) {
        list.push("No hay firmantes. Agrega al menos uno.");
      }
      if (!this.document.expires_at) {
        list.push("No se configuró fecha de expiración.");
      }
      const emails = this.signers.map(s => s.email);
      const dupes = emails.filter((e, i) => emails.indexOf(e) !== i);
      if (dupes.length) {
        list.push("Hay emails duplicados entre los firmantes.");
      }
      return list;
    }
  },
  methods: {
    readType(type) {
      const map = {
        contrato: "Contrato",
        nda: "NDA",
        propuesta: "Propuesta",
        alcance: "Alcance técnico",
        otro: "Otro"
      };
      return map[type] || type;
    },
    readSource(source) {
      const map = {
        template: "Plantilla",
        scratch: "Desde cero",
        upload: "PDF cargado"
      };
      return map[source] || source;
    },
    confirmSend() {
      if (!this.canSend) return;
      this.$emit("send", {
        email_message: this.emailMessage || null,
        reminder_days: this.reminderDays || null
      });
    },
    handleClose() {
      this.visible = false;
      this.$emit("cancel");
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.emailMessage = "";
        this.reminderDays = 3;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.send-review-dialog {
  ::v-deep .el-dialog__header {
    display: none;
  }
  ::v-deep .el-dialog__body {
    padding: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
}

.send-review {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #e4eaf6;
    background: #fff;
    flex-shrink: 0;
  }
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #31355c;
    margin: 0;
  }
  &__close {
    background: #31355c;
    color: #fff;
    border: none;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    &:hover {
      opacity: 0.85;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 30px 24px;
    background: #f7f8f9;
  }
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    max-width: 900px;
    margin: 0 auto;
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__card {
    background: #fff;
    border: 1px solid #e4eaf6;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 0 10px 0 #d6e4f3;
    &--warning {
      border-color: #faeeda;
      background: #fffbf5;
    }
  }
  &__card-title {
    font-size: 14px;
    font-weight: 600;
    color: #31355c;
    margin: 0 0 14px;
    i {
      color: #f59e0b;
      margin-right: 4px;
    }
  }

  &__row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid #e4eaf6;
    &:last-child {
      border-bottom: none;
    }
  }
  &__label {
    font-size: 13px;
    color: #989aae;
  }
  &__value {
    font-size: 13px;
    color: #31355c;
    font-weight: 500;
  }

  &__signer {
    display: flex;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #e4eaf6;
    &:last-child {
      border-bottom: none;
    }
  }
  &__signer-order {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e4eaf6;
    color: #31355c;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    margin-right: 10px;
    flex-shrink: 0;
  }
  &__signer-info {
    flex: 1;
    min-width: 0;
  }
  &__signer-name {
    font-size: 13px;
    font-weight: 500;
    color: #31355c;
    margin: 0;
  }
  &__signer-email {
    font-size: 11px;
    color: #989aae;
    margin: 2px 0 0;
  }
  &__signer-method {
    font-size: 11px;
    color: #7e819f;
    background: #e4eaf6;
    padding: 2px 8px;
    border-radius: 8px;
    flex-shrink: 0;
    margin-left: 8px;
  }

  &__empty {
    text-align: center;
    padding: 20px 0;
    i {
      font-size: 28px;
      color: #989aae;
      margin-bottom: 8px;
    }
    p {
      font-size: 13px;
      color: #989aae;
      margin: 4px 0;
    }
  }

  &__warnings {
    margin: 0;
    padding: 0 0 0 18px;
    li {
      font-size: 13px;
      color: #854f0b;
      padding: 3px 0;
    }
  }
}

::v-deep .el-textarea__inner {
  border: 1.5px solid #e4eaf6;
  border-radius: 10px;
  background: #fafbfd;
  font-size: 13px;
  &:focus {
    border-color: #3fdbbf;
    background: #fff;
  }
}

::v-deep .el-select .el-input__inner {
  border: 1.5px solid #e4eaf6;
  border-radius: 10px;
  background: #fafbfd;
  font-size: 13px;
  min-height: 44px;
  &:focus {
    border-color: #3fdbbf;
    background: #fff;
  }
}
</style>
